import React from 'react';
import { Product, WithContext } from 'schema-dts';
import { ReviewRatingStatistics } from 'shared/types/product/Product';
import { ExtendedVariant } from 'shared/types/product/Variant';
import { UIProduct } from 'components/commercetools-ui/organisms/product/product-details/types';
import { mapProductImages, mapProductOffer, mapProductRatings, mapProductReview } from './utils';
import { ReviewListProps } from '../../../components/commercetools-ui/organisms/review-list';
import { textToColor } from '../../textToColor/textToColor';

export interface ProductJSONLDProps {
  variant: ExtendedVariant;
  product: UIProduct;
  description?: string
  ratingData?: ReviewRatingStatistics;
  finalBasePrice: number;
  reviews?: ReviewListProps;
  origin?: string;
  locale: string;
}

/**
 * SEO Data will be generated for the master variant, which is the first
 * @param product
 * @param reviews
 * @param ratingData
 * @param locale
 * @constructor
 */
export const ProductJSONLD = ({
  product,
  reviews,
  description,
  ratingData,
  origin,
  variant,
  finalBasePrice,
}: ProductJSONLDProps) => {
  const image: Product['image'] = mapProductImages(product);
  const offers: Product['offers'] = mapProductOffer(product, variant, finalBasePrice, origin);
  const color: Product['color'] = textToColor(variant.attributes?.color).label;

  const productJsonLd: WithContext<Product> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    sku: variant.sku,
    offers,
    image,
    ...(color && { color }),
    brand: {
      '@type': 'Brand',
      name: 'Viewrail',
    },
    itemCondition: 'https://schema.org/NewCondition',
    description,
    ...(ratingData && { aggregateRating: mapProductRatings(ratingData) }),
    ...(reviews && { review: mapProductReview(reviews) }),
  };

  {
    /* script with dangerously set inner html as based on the NextJS site:
          https://nextjs.org/docs/app/building-your-application/optimizing/metadata#json-ld
          */
  }
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(productJsonLd) }} />;
};
